import { Reducer } from 'redux'

import { LOGIN_ACTION, LoginAction } from 'actions/authorization/loginAction'
import { LOGOUT_ACTION, LogoutAction } from 'actions/authorization/logoutAction'
import {
  NORMALIZE_PHOTO_RATING_SETTINGS,
  PhotoRatingsSettingsTypes,
} from 'actions/photoRating/photoRatingSettingsAction'
import {
  FETCH_ASK_PHOTO,
  FetchAskPhotoAction,
} from 'actions/rating/checkIfAskPhotoNeededAction'
import {
  DISLIKE_PHOTO,
  DisLikePhotoAction,
} from 'actions/rating/dislikePhotoAction'
import {
  FETCH_VOTING_PHOTOS,
  FetchVotingPhotosAction,
} from 'actions/rating/fetchVotingPhotosAction'
import {
  SET_ACTIVE_KEYBOARD_BUTTON,
  SetActiveKeyboardButton,
} from 'actions/rating/keyboardAction'
import { LIKE_PHOTO, LikePhotoAction } from 'actions/rating/likePhotoAction'
import {
  CLEAR_VOTING_PHOTOS,
  RatingTypes,
  REVERT_LAST_PHOTO,
  TOGGLE_TUTORIAL_VISIBLE,
} from 'actions/rating/photoRatingAction'
import {
  CLEAR_PROMO,
  INCREMENT_LIKED_COUNTER,
  INCREMENT_VOTED_COUNTER,
  PhotoRatingPromoActionTypes,
  SET_FEATURED_ACTIVE_PHOTOS,
  SET_RATING_PROMO,
  UPDATE_LAST_INCOGNITO_NOTICE_SHOW,
} from 'actions/rating/promoAction'
import {
  LocalStorageActionTypes,
  RESTORE_PARTIAL_STATE,
} from 'actions/system/localStorageAction'
import {
  RESTORE_INITIAL_STATE,
  RestoreInitialStateAction,
} from 'actions/system/restoreInitialStateAction'
import { initialStateReducer } from 'reducers/default/initialStateReducer'
import { defaultPromiseReducer } from 'reducers/defaultPromiseReducer'
import { changePhotosStateBeforeFetch } from 'reducers/rating/changePhotosStateBeforeFetch'
import { deleteDuplicatePhotos } from 'reducers/rating/deleteDuplicatePhotos'
import {
  ButtonType,
  initialState,
  PhotoRatingState,
} from 'reducers/rating/PhotoRatingState'

export const photoRatingReducer: Reducer<
  PhotoRatingState,
  | FetchVotingPhotosAction
  | RatingTypes
  | PhotoRatingPromoActionTypes
  | LikePhotoAction
  | DisLikePhotoAction
  | SetActiveKeyboardButton
  | LoginAction
  | LogoutAction
  | FetchAskPhotoAction
  | LocalStorageActionTypes
  | RestoreInitialStateAction
  | PhotoRatingsSettingsTypes
> = (state = initialState, action) => {
  switch (action.type) {
    case FETCH_VOTING_PHOTOS:
      return defaultPromiseReducer(
        state,
        action,
        () => ({
          error: undefined,
          errorStatus: undefined,
          votingPhotosLoaded: false,
          votingPhotosFetchingFailed: false,
        }),
        (result) => {
          if (result) {
            return {
              votingPhotos: deleteDuplicatePhotos(
                state.votingPhotos,
                result.photos
              ),
              updatedFullVotingPhotos: deleteDuplicatePhotos(
                state.votingPhotos,
                result.photos
              ),
              votingPhotosLoaded: true,
              votingPhotosFetchingFailed: false,
            }
          }

          return state
        },
        (result, state, error, errorStatus) => ({
          votingPhotosLoaded: true,
          votingPhotosFetchingFailed: true,
          error,
          errorStatus,
        })
      )

    case LIKE_PHOTO:
      return defaultPromiseReducer(
        state,
        action,
        () => {
          return changePhotosStateBeforeFetch(state, ButtonType.Liking)
        },
        (result) => {
          if (result) {
            return {
              voting: false,
              liking: false,
              ...result,
            }
          }
          return state
        },
        (result, state, error, errorStatus) => ({
          voting: false,
          liking: false,
          error,
          errorStatus,
        })
      )

    case DISLIKE_PHOTO:
      return defaultPromiseReducer(
        state,
        action,
        () => {
          return changePhotosStateBeforeFetch(state, ButtonType.Disliking)
        },
        (result) => {
          if (result) {
            return {
              voting: false,
              disliking: false,
            }
          }

          return state
        },
        (result, state, error, errorStatus) => ({
          voting: false,
          disliking: false,
          error,
          errorStatus,
        })
      )

    case REVERT_LAST_PHOTO:
      const updatedPastVotingPhotos = [...state.pastVotingPhotos]
      const lastVotedPhoto = updatedPastVotingPhotos.pop()!

      return {
        ...state,
        voting: false,
        disliking: false,
        votingPhotos: [lastVotedPhoto, ...state.votingPhotos],
        pastVotingPhotos: updatedPastVotingPhotos,
        revertCounter:
          state.revertCounter === null ? 1 : state.revertCounter + 1,
      }

    case CLEAR_VOTING_PHOTOS:
      const votingPhotos =
        state.revertCounter !== null
          ? state.votingPhotos.slice(0, state.revertCounter)
          : []

      return {
        ...initialState,
        votingPhotos,
        pastVotingPhotos: state.pastVotingPhotos,
        votedCounter: state.votedCounter,
        revertCounter: state.revertCounter,
        currentPromo: state.currentPromo,
        currentPromoIndex: state.currentPromoIndex,
        likedCounter: state.likedCounter,
        lastIncognitoNoticeShownAtLikedCount:
          state.lastIncognitoNoticeShownAtLikedCount,
      }

    case LOGOUT_ACTION:
      return defaultPromiseReducer(state, action, undefined, () => ({
        ...initialState,
      }))

    case LOGIN_ACTION:
      return defaultPromiseReducer(state, action, undefined, () => ({
        ...initialState,
      }))

    case SET_RATING_PROMO:
      return {
        ...state,
        currentPromoIndex: action.index,
        currentPromo: action.promo,
      }

    case SET_FEATURED_ACTIVE_PHOTOS:
      return { ...state, featuredPhotosActive: action.value }

    case CLEAR_PROMO:
      return {
        ...state,
        currentPromoIndex: 0,
        currentPromo: null,
        votedCounter: 0,
      }

    /**
     * Это для рекламы и notice.
     * Когда мы идем назад, данный case не будет работать.
     */
    case INCREMENT_VOTED_COUNTER:
      return {
        ...state,
        votedCounter: state.votedCounter + 1,
      }

    case INCREMENT_LIKED_COUNTER:
      if (state.revertCounter === null) {
        return {
          ...state,
          likedCounter: state.likedCounter + 1,
        }
      }

      return state

    case UPDATE_LAST_INCOGNITO_NOTICE_SHOW:
      return {
        ...state,
        lastIncognitoNoticeShownAtLikedCount: state.likedCounter,
      }

    case TOGGLE_TUTORIAL_VISIBLE:
      return {
        ...state,
        isTutorialVisible: action.isTutorialVisible,
      }

    case FETCH_ASK_PHOTO:
      return defaultPromiseReducer(state, action, undefined, (result) => {
        if (result) {
          return {
            shouldAskPhoto: {
              ...state.shouldAskPhoto,
              showUploader: result.showUploader,
            },
          }
        }

        return null
      })

    case SET_ACTIVE_KEYBOARD_BUTTON:
      return {
        ...state,
        [action.button]: true,
      }

    case RESTORE_PARTIAL_STATE:
      if (action.name === nameReducer) {
        return {
          ...state,
          ...action.partialState.photoRatingReducer,
        }
      }

      return state

    case NORMALIZE_PHOTO_RATING_SETTINGS:
      return defaultPromiseReducer(state, action, undefined, (result) => {
        if (result) {
          return { ...initialState }
        }

        return state
      })

    case RESTORE_INITIAL_STATE:
      return initialStateReducer(state, action, nameReducer)

    default:
      return state
  }
}

const nameReducer = 'photoRatingReducer'
